/**
 * Directive Parser
 * ----------------
 * Turns lesson Markdown into an ordered list of blocks: `prose` (plain Markdown,
 * rendered safely elsewhere) and `directive` (a named interaction plus a small,
 * explicit configuration). A directive is only a *reference* to a registered
 * component — this parser never evaluates scripts, JSX, HTML, or any embedded
 * JavaScript. It only reads text into data.
 *
 * Authoring syntax is documented in `docs/lesson-format.md`. The implementation
 * is deliberately a small hand-written parser rather than a rigid universal
 * block schema, so lesson Markdown stays readable.
 */

/** Scalar or list values allowed in directive configuration and front matter. */
export type DirectiveValue = string | number | boolean | string[]

export type DirectiveConfig = Record<string, DirectiveValue>

export interface ProseBlock {
  kind: 'prose'
  markdown: string
}

export interface DirectiveBlock {
  kind: 'directive'
  /** Resolved component/registry name, e.g. `multiple-choice`. */
  name: string
  /** Stable interaction id, from `id:` or derived deterministically by order. */
  id: string
  config: DirectiveConfig
  /** Original directive text, shown verbatim in the unknown-directive fallback. */
  raw: string
}

export type LessonBlock = ProseBlock | DirectiveBlock

export interface LessonFrontMatter {
  id?: string
  title?: string
  estimatedMinutes?: number
  /** Author-only note — never rendered to the learner. */
  notes?: string
  /** Author-only sources — never rendered to the learner. */
  sources?: string[]
  [key: string]: DirectiveValue | undefined
}

export interface ParsedLesson {
  meta: LessonFrontMatter
  blocks: LessonBlock[]
}

export function parseLesson(markdown: string): ParsedLesson {
  const { meta, body } = extractFrontMatter(markdown)
  return { meta, blocks: parseBlocks(body) }
}

/** Split an optional leading `--- ... ---` front-matter section from the body. */
export function extractFrontMatter(markdown: string): {
  meta: LessonFrontMatter
  body: string
} {
  const lines = markdown.replace(/^\uFEFF/, '').split(/\r?\n/)
  let start = 0
  while (start < lines.length && lines[start].trim() === '') start++

  if (lines[start]?.trim() !== '---') {
    return { meta: {}, body: markdown }
  }

  const end = lines.findIndex((line, index) => index > start && line.trim() === '---')
  if (end === -1) {
    return { meta: {}, body: markdown }
  }

  const meta = parseConfig(lines.slice(start + 1, end)) as LessonFrontMatter
  const body = lines.slice(end + 1).join('\n')
  return { meta, body }
}

function parseBlocks(body: string): LessonBlock[] {
  const lines = body.split(/\r?\n/)
  const blocks: LessonBlock[] = []
  const directiveCounts: Record<string, number> = {}
  let prose: string[] = []
  let inCodeFence = false

  const flushProse = () => {
    const text = prose.join('\n').trim()
    if (text) blocks.push({ kind: 'prose', markdown: text })
    prose = []
  }

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]
    const trimmed = line.trim()

    // Never interpret directives inside fenced code blocks — code is prose.
    if (trimmed.startsWith('```')) {
      inCodeFence = !inCodeFence
      prose.push(line)
      continue
    }

    const open = !inCodeFence ? /^:::(\S+)(.*)$/.exec(trimmed) : null
    if (open) {
      flushProse()
      const rawName = open[1]
      const inlineAttrs = parseInlineAttributes(open[2])

      const bodyLines: string[] = []
      let j = i + 1
      while (j < lines.length && lines[j].trim() !== ':::') {
        bodyLines.push(lines[j])
        j++
      }

      const config: DirectiveConfig = { ...inlineAttrs, ...parseConfig(bodyLines) }

      // A generic `:::interaction name="x"` resolves to component `x`.
      const name =
        rawName === 'interaction' && typeof config.name === 'string' ? config.name : rawName

      const ordinal = (directiveCounts[name] = (directiveCounts[name] ?? 0) + 1)
      const id = typeof config.id === 'string' && config.id ? config.id : `${name}-${ordinal}`
      const raw = [line, ...bodyLines, ':::'].join('\n')

      blocks.push({ kind: 'directive', name, id, config, raw })
      i = j // continue after the closing fence (loop's i++ steps past it)
      continue
    }

    prose.push(line)
  }

  flushProse()
  return blocks
}

/** Parse `key="value"` pairs from a directive's opening-fence remainder. */
function parseInlineAttributes(rest: string): DirectiveConfig {
  const attrs: DirectiveConfig = {}
  const pattern = /([A-Za-z_][\w-]*)="([^"]*)"/g
  let match: RegExpExecArray | null
  while ((match = pattern.exec(rest)) !== null) {
    attrs[match[1]] = match[2]
  }
  return attrs
}

/**
 * Parse a small, explicit YAML-like subset used by both front matter and
 * directive bodies: `key: scalar` lines and `key:` followed by indented
 * `- item` lines. Malformed lines are ignored rather than executed.
 */
export function parseConfig(lines: string[]): DirectiveConfig {
  const config: DirectiveConfig = {}

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]
    if (line.trim() === '') continue

    const match = /^([A-Za-z0-9_-]+):\s*(.*)$/.exec(line)
    if (!match) continue

    const key = match[1]
    const rest = match[2]

    if (rest.trim() === '') {
      const items: string[] = []
      let j = i + 1
      while (j < lines.length) {
        const item = /^\s*-\s+(.*)$/.exec(lines[j])
        if (!item) break
        items.push(stripQuotes(item[1].trim()))
        j++
      }
      if (items.length > 0) {
        config[key] = items
        i = j - 1
      } else {
        config[key] = ''
      }
    } else {
      config[key] = coerceScalar(rest.trim())
    }
  }

  return config
}

function coerceScalar(value: string): DirectiveValue {
  const unquoted = stripQuotes(value)
  if (unquoted !== value) return unquoted // was quoted → always a string
  if (value === 'true') return true
  if (value === 'false') return false
  if (/^-?\d+(\.\d+)?$/.test(value)) return Number(value)
  return value
}

function stripQuotes(value: string): string {
  if (value.length >= 2) {
    const first = value[0]
    const last = value[value.length - 1]
    if ((first === '"' && last === '"') || (first === "'" && last === "'")) {
      return value.slice(1, -1)
    }
  }
  return value
}
